/**
 * Vue Router
 *
 * @library
 *
 * https://router.vuejs.org/en/
 */

// Lib imports
import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';
import { getSentryEnv } from '@/sentry';

// Routes
import paths from './paths';

function route(path, view, name, meta, alias, redirect) {
  // "feature-flag": exclude this route on certain environments
  if (meta && meta.excludeEnv && meta.excludeEnv.findIndex((env) => env === getSentryEnv()) >= 0) {
    return null;
  }
  return {
    name: name || view,
    path,
    meta,
    alias,
    redirect,
    component: redirect ? undefined : (resolve) => import(`@/views/${view}.vue`).then(resolve),
  };
}

Vue.use(Router);

// Create a new router
const router = new Router({
  mode: 'hash',
  routes: paths
    .map((path) => route(path.path, path.view, path.name, path.meta, path.alias, path.redirect))
    .filter((path) => path)
    .concat([{ path: '*', redirect: '/error' }]),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { selector: to.hash };
    }
    return { x: 0, y: 0 };
  },
});

// Replace the original router methods with ones that catch isNavigationFailure errors
// and output them to the console instead of throwing them, so they don't land in Sentry
// See: https://v3.router.vuejs.org/guide/advanced/navigation-failures.html
function createRouterFn(fn) {
  return function routerFn(location, onComplete, onAbort) {
    if (onComplete || onAbort) {
      return this[fn](location, onComplete, onAbort);
    }
    return this[fn](location).catch((err) => {
      if (Router.isNavigationFailure(err)) {
        // Ignore NavigationFailure errors and log them to the console
        console.warn(err);
      } else {
        // Re-throw any other errors
        throw err;
      }
    });
  };
}

router.pushOriginal = router.push;
router.replaceOriginal = router.replace;

router.push = createRouterFn('pushOriginal');
router.replace = createRouterFn('replaceOriginal');

Vue.use(Meta);

export default router;
